/**
 * React 19 compatibility shims for `semantic-ui-react`.
 *
 * React 19 no longer applies a function component's static `defaultProps`.
 * semantic-ui-react (0.88.x) still declares `defaultProps` on 25 of its
 * function components, so those defaults silently become `undefined`.
 *
 * Most are harmless: SUIR's `getElementType()` reads `Component.defaultProps.as`
 * directly off the static rather than out of `props`, so plain `as:` defaults
 * (`Table.Cell` -> 'td', `Message.Item` -> 'li', ...) still resolve. The
 * components re-exported here are the ones that destructure the value out of
 * `props`, where losing it changes behavior:
 *
 *   - `Form.Input` and friends lose `control`, so `FormField` takes its
 *     `_isNil(control)` branch and renders the label with no input at all.
 *   - `Table.HeaderCell` loses `as: 'th'` and forwards `as={undefined}` to
 *     `Table.Cell`, whose own default then wins -> renders `<td>`.
 *   - `Table.Footer` loses `as: 'tfoot'` the same way -> renders `<thead>`.
 *   - `Radio` loses `type: 'radio'`, so the `Checkbox` it delegates to falls
 *     back to its own `type: 'checkbox'` -> loses radio-group exclusivity.
 *
 * `Tab.Pane` has the same defect (it loses `active: true`) but is fixed at its
 * call sites separately.
 *
 * Import these in place of the `semantic-ui-react` originals. They are
 * drop-in: the full component namespace is preserved.
 */
import React from 'react';
import {
  Form as SUIForm,
  Radio as SUIRadio,
  Table as SUITable,
} from 'semantic-ui-react';

/**
 * Re-applies a component's static `defaultProps`. Spread before `props` so
 * explicit props (and spreads) still win — the precedence `defaultProps` had.
 */
function withDefaultProps<C extends React.ComponentType<any>>(Component: C): C {
  const Rendered = Component as React.ComponentType<any>;
  const defaults =
    (Component as {defaultProps?: Record<string, unknown>}).defaultProps ?? {};

  const Wrapped = (props: React.ComponentProps<C>) => (
    <Rendered {...defaults} {...props} />
  );
  Wrapped.displayName = `withDefaultProps(${
    Rendered.displayName ?? Rendered.name ?? 'Component'
  })`;

  return Wrapped as unknown as C;
}

export const Form = Object.assign(
  (props: React.ComponentProps<typeof SUIForm>) => <SUIForm {...props} />,
  SUIForm,
  {
    Button: withDefaultProps(SUIForm.Button),
    Checkbox: withDefaultProps(SUIForm.Checkbox),
    Dropdown: withDefaultProps(SUIForm.Dropdown),
    Input: withDefaultProps(SUIForm.Input),
    Radio: withDefaultProps(SUIForm.Radio),
    Select: withDefaultProps(SUIForm.Select),
    TextArea: withDefaultProps(SUIForm.TextArea),
  }
) as typeof SUIForm;

export const Table = Object.assign(
  (props: React.ComponentProps<typeof SUITable>) => <SUITable {...props} />,
  SUITable,
  {
    Footer: withDefaultProps(SUITable.Footer),
    HeaderCell: withDefaultProps(SUITable.HeaderCell),
  }
) as typeof SUITable;

export const Radio = withDefaultProps(SUIRadio);
